/**
 * KNK-1.44 / FR-35 — the flag review queue: parsing, copy and the visibility rule.
 *
 * The server decides everything that matters (migration 20261011a7): what is in the queue (live
 * verification flags, Do Not Knock markings over the daily limit, a rep's request), who reaches a
 * review (knock.review plus .team over their teams or .all, never their own), what confirm and
 * restore do to the Knock, escalation after 7 days, and the SM-C5 false-flag rate. Nothing here
 * is the gate.
 *
 * ⚠️ Confirm makes the Knock `rejected`, the only state pay and Score may exclude. Restore makes
 * it `verified` (reason `restored_on_review`). A Do Not Knock or session review changes nothing
 * else: reversing a Do Not Knock is still remove_do_not_knock's.
 */
import type { KnockModeStatus } from '@/lib/knockMode';

export type ReviewSubject = 'knock' | 'dnk' | 'session';
export type ReviewSource = 'verification' | 'dnk_daily' | 'rep_request';
export type ReviewState = 'open' | 'confirmed' | 'restored';
export type ReviewEventKind = 'opened' | 'note' | 'confirmed' | 'restored' | 'request' | 'escalated';
export type QueueState = 'open' | 'resolved';

export interface ReviewKnock {
  id: string;
  deviceAt: string | null;
  statusCode: string | null;
  disposition: string | null;
  verification: string | null;
  verificationReason: string | null;
  distanceM: number | null;
  accuracyM: number | null;
  radiusM: number | null;
  mockLocation: boolean | null;
  dwellMs: number | null;
  voidedAt: string | null;
  imported: boolean;
  late: boolean;
}

export interface ReviewDnk {
  id: string;
  reason: string | null;
  markedAt: string | null;
  localDay: string | null;
  removedAt: string | null;
}

export interface ReviewSession {
  id: string;
  startedAt: string | null;
  endedAt: string | null;
}

export interface ReviewEvent {
  kind: ReviewEventKind;
  at: string | null;
  body: string | null;
  byName: string | null;
}

export interface ReviewItem {
  id: string;
  subject: ReviewSubject;
  source: ReviewSource;
  state: ReviewState;
  reason: string | null;
  openedAt: string | null;
  ageDays: number;
  escalatedAt: string | null;
  repRequestedAt: string | null;
  resolvedAt: string | null;
  resolvedByName: string | null;
  repId: string | null;
  repName: string;
  doorId: string | null;
  address: string | null;
  knock: ReviewKnock | null;
  dnk: ReviewDnk | null;
  session: ReviewSession | null;
  events: ReviewEvent[];
}

export interface ReviewSummary {
  open: number;
  escalated: number;
  confirmed30d: number;
  restored30d: number;
  falseFlagRate30d: number | null;
}

export interface ReviewQueue {
  asOf: string | null;
  state: QueueState;
  escalateAfterDays: number;
  summary: ReviewSummary;
  items: ReviewItem[];
}

/** SM-C5: restores above 5% of decided flags means verification is unfair. */
export const FALSE_FLAG_TARGET = 0.05;
export const NOTE_MAX = 1000;

export const KNOCK_REVIEW_COPY = {
  pageTitle: 'Flag review',
  pageSubtitle: 'Knocks the app flagged, Do Not Knock markings over the daily limit, and reps who asked for a review.',
  fairness: 'Nothing here reduces pay or Score until someone confirms it. Restore a flag that is wrong.',
  off: 'Flag review is for people who review Knocks, in an account with Knock Mode on.',
  emptyOpen: 'Nothing is waiting for review.',
  emptyResolved: 'Nothing was decided in the last 30 days.',
  confirmNeedsNote: 'Say why you are confirming. The rep’s manager reads it, and it can affect pay.',
  noteTooLong: `Keep the note under ${NOTE_MAX.toLocaleString()} characters.`,
  confirmHelpKnock: 'Confirm marks the Knock rejected. It no longer counts toward pay or Score.',
  restoreHelpKnock: 'Restore marks the Knock verified. It counts as normal.',
  dnkHelp: 'Deciding records your judgement only. The Door stays Do Not Knock until someone with the right to reverse it does.',
  sessionHelp: 'Deciding records your judgement only. Each flagged Knock in the session has its own review.',
  escalatedBadge: 'Escalated',
  requestedBadge: 'Rep asked',
  undone: 'The rep undid this Knock.',
} as const;

const REASON_LABEL: Record<string, string> = {
  mock_location: 'The phone reported a mock location',
  impossible_movement: 'Moved faster than walking between Doors',
  attestation_failed: 'The phone failed its integrity check',
  too_far: 'Too far from the Door',
  door_location_uncertain: 'Door location uncertain',
  door_not_located: 'The Door has no position',
  low_accuracy: 'Location too coarse to tell',
  no_location: 'No location with the Knock',
  restored_on_review: 'Restored on review',
  imported: 'Imported',
  flagged: 'Flagged',
};

/** A verification reason code in words. A sentence (a Do Not Knock's reason) passes through. */
export function reasonLabel(code: string | null): string {
  if (!code) return 'Flagged';
  if (REASON_LABEL[code]) return REASON_LABEL[code];
  if (!/^[a-z_]+$/.test(code)) return code;
  const words = code.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function subjectLabel(item: Pick<ReviewItem, 'subject' | 'source'>): string {
  if (item.source === 'rep_request') {
    return item.subject === 'session' ? 'Rep asked for review of a session' : 'Rep asked for review of a Knock';
  }
  if (item.subject === 'dnk') return 'Do Not Knock over the daily limit';
  return 'Flagged Knock';
}

export function ageLine(item: Pick<ReviewItem, 'ageDays' | 'escalatedAt'>): string {
  const d = item.ageDays;
  const base = d <= 0 ? 'Opened today' : d === 1 ? 'Open 1 day' : `Open ${d} days`;
  return item.escalatedAt ? `${base} · escalated` : base;
}

/** What the database knows about the Knock, one fact per line. Live evidence only. */
export function evidenceLines(item: Pick<ReviewItem, 'knock'>): string[] {
  const k = item.knock;
  if (!k) return [];
  const out: string[] = [];
  if (k.distanceM !== null) {
    const allowed = k.radiusM !== null ? ` (allowed ${k.radiusM} m)` : '';
    const fix = k.accuracyM !== null ? `, fix within ${Math.round(k.accuracyM)} m` : '';
    out.push(`${Math.round(k.distanceM)} m from the Door${allowed}${fix}`);
  } else if (k.accuracyM === null) {
    out.push(REASON_LABEL.no_location);
  }
  if (k.mockLocation) out.push(REASON_LABEL.mock_location);
  if (k.dwellMs !== null) out.push(`On the porch ${Math.max(1, Math.round(k.dwellMs / 1000))} s`);
  return out;
}

/** The trail, minus the 'opened' body, which repeats the reason already shown above it. */
export function trailEvents(item: Pick<ReviewItem, 'events'>): ReviewEvent[] {
  return item.events.map((e) => (e.kind === 'opened' ? { ...e, body: null } : e));
}

export function falseFlagLine(s: ReviewSummary): string {
  const decided = s.confirmed30d + s.restored30d;
  if (s.falseFlagRate30d === null || decided === 0) return 'False-flag rate, last 30 days: no flags decided yet';
  const pct = Math.round(s.falseFlagRate30d * 1000) / 10;
  return `False-flag rate, last 30 days: ${pct}% (${s.restored30d} of ${decided} flags restored)`;
}

export function falseFlagOverTarget(s: ReviewSummary): boolean {
  return s.falseFlagRate30d !== null && s.falseFlagRate30d > FALSE_FLAG_TARGET;
}

/** Why Confirm is disabled, or null when it may go. The database enforces the same rule. */
export function confirmBlockedReason(note: string): string | null {
  const t = note.trim();
  if (t.length === 0) return KNOCK_REVIEW_COPY.confirmNeedsNote;
  if (t.length > NOTE_MAX) return KNOCK_REVIEW_COPY.noteTooLong;
  return null;
}

/**
 * Should the sidebar offer the queue, and should the page render it? Knock Mode on AND a review
 * rung (knock.review.team / .all; a superadmin through system.override). Nothing while loading.
 */
export function knockReviewVisible(input: { status: KnockModeStatus; loading: boolean; canView: boolean }): boolean {
  if (input.loading || !input.status.enabled) return false;
  return input.canView;
}

export const KNOCK_REVIEW_PERMISSIONS = ['knock.review.team', 'knock.review.all', 'system.override'] as const;

/** The server's "this is not for you": no review rung (42501) or Knock Mode off (KM001). */
export function isKnockReviewRefusal(e: unknown): boolean {
  const code = isRecord(e) ? e.code : undefined;
  return code === '42501' || code === 'KM001';
}

/* -- parsing ----------------------------------------------------------------- */

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(v as T) ? (v as T) : fallback;
}

const SUBJECTS: readonly ReviewSubject[] = ['knock', 'dnk', 'session'];
const SOURCES: readonly ReviewSource[] = ['verification', 'dnk_daily', 'rep_request'];
const STATES: readonly ReviewState[] = ['open', 'confirmed', 'restored'];
const KINDS: readonly ReviewEventKind[] = ['opened', 'note', 'confirmed', 'restored', 'request', 'escalated'];

function parseKnock(v: unknown): ReviewKnock | null {
  if (!isRecord(v) || !str(v.id)) return null;
  return {
    id: str(v.id) as string,
    deviceAt: str(v.device_at),
    statusCode: str(v.status_code),
    disposition: str(v.disposition),
    verification: str(v.verification),
    verificationReason: str(v.verification_reason),
    distanceM: num(v.distance_m),
    accuracyM: num(v.accuracy_m),
    radiusM: num(v.radius_m),
    mockLocation: bool(v.mock_location),
    dwellMs: num(v.dwell_ms),
    voidedAt: str(v.voided_at),
    imported: v.imported === true,
    late: v.late === true,
  };
}

function parseDnk(v: unknown): ReviewDnk | null {
  if (!isRecord(v) || !str(v.id)) return null;
  return {
    id: str(v.id) as string,
    reason: str(v.reason),
    markedAt: str(v.marked_at),
    localDay: str(v.local_day),
    removedAt: str(v.removed_at),
  };
}

function parseSession(v: unknown): ReviewSession | null {
  if (!isRecord(v) || !str(v.id)) return null;
  return { id: str(v.id) as string, startedAt: str(v.started_at), endedAt: str(v.ended_at) };
}

export function parseReviewItem(v: unknown): ReviewItem | null {
  if (!isRecord(v)) return null;
  const id = str(v.id);
  if (!id) return null;
  const events = Array.isArray(v.events) ? v.events : [];
  return {
    id,
    subject: oneOf(v.subject, SUBJECTS, 'knock'),
    source: oneOf(v.source, SOURCES, 'verification'),
    // Never resolved by default: an unreadable state must not hide a review from the queue.
    state: oneOf(v.state, STATES, 'open'),
    reason: str(v.reason),
    openedAt: str(v.opened_at),
    ageDays: num(v.age_days) ?? 0,
    escalatedAt: str(v.escalated_at),
    repRequestedAt: str(v.rep_requested_at),
    resolvedAt: str(v.resolved_at),
    resolvedByName: str(v.resolved_by_name),
    repId: str(v.rep_id),
    repName: str(v.rep_name) ?? 'Rep',
    doorId: str(v.door_id),
    address: str(v.address),
    knock: parseKnock(v.knock),
    dnk: parseDnk(v.dnk),
    session: parseSession(v.session),
    events: events.filter(isRecord).map((e) => ({
      kind: oneOf(e.kind, KINDS, 'note'),
      at: str(e.at),
      body: str(e.body),
      byName: str(e.by_name),
    })),
  };
}

export function parseKnockReviewQueue(raw: unknown): ReviewQueue {
  const o = isRecord(raw) ? raw : {};
  const s = isRecord(o.summary) ? o.summary : {};
  const list = Array.isArray(o.items) ? o.items : [];
  return {
    asOf: str(o.as_of),
    state: o.state === 'resolved' ? 'resolved' : 'open',
    escalateAfterDays: num(o.escalate_after_days) ?? 7,
    summary: {
      open: num(s.open) ?? 0,
      escalated: num(s.escalated) ?? 0,
      confirmed30d: num(s.confirmed_30d) ?? 0,
      restored30d: num(s.restored_30d) ?? 0,
      falseFlagRate30d: num(s.false_flag_rate_30d),
    },
    items: list.map(parseReviewItem).filter((i): i is ReviewItem => i !== null),
  };
}
